import { useMemo } from 'react';
import type { CreateAccountsSettingsTypeItem } from '@/shared/types';
import type { OpenAccountType } from './types';
import type { CreateAccountsSettingsDictionary } from '@/shared/types';
import { useAssets, useAssetsIcons } from '@/shared/api';
import { useAccountsRests } from '@/entities/account';
import { usePendingAccountsStore } from './pendingAccountsStore';

const useOpenAccountFormData = (
  accountType: OpenAccountType,
  createAccountsSettings: CreateAccountsSettingsDictionary
) => {
  const { data: accountsRests } = useAccountsRests();
  const { pendingAccounts } = usePendingAccountsStore();
  const { data: assetsData } = useAssets({
    enabled: ['invest', 'margin', 'earn'].includes(accountType),
  });

  const { assetsIconsData } = useAssetsIcons({
    enabled: !!assetsData,
  });

  const accountSettings = useMemo(() => {
    if (accountType === 'margin') {
      return createAccountsSettings.leverage;
    }
    return createAccountsSettings[accountType];
  }, []);

  const formFields = useMemo(() => {
    const fields = [];
    const { type, assets, leverage } = accountSettings;

    if (type && type.length > 1) {
      fields.push('tradingProductCode');
    }

    if (assets && assets.length > 0) {
      fields.push('asset');
    }

    if (leverage && leverage.length > 1) {
      fields.push('leverageValue');
    }

    if (
      createAccountsSettings?.eula != '' &&
      createAccountsSettings?.eula != ' '
    ) {
      fields.push('eula');
    }

    return fields;
  }, [accountSettings, createAccountsSettings]);

  const getTypeSelectItems = (
    type: Array<CreateAccountsSettingsTypeItem> | undefined
  ) => {
    if (!type) return [];
    return type.map(item => ({
      value: item.typeCode,
      typeCode: item.typeCode,
      typeDescription: item.typeDescription,
    }));
  };

  const takenAssets = useMemo(() => {
    if (accountType === 'earn') {
      const existingAssets =
        accountsRests?.earn?.accounts
          .map(account => account.balanceAsset?.code)
          .filter((code): code is string => !!code) || [];
      const pendingAssets = pendingAccounts
        .filter(account => account.accountType === 'earn')
        .map(account => account.assetCode);
      return [...existingAssets, ...pendingAssets];
    }

    if (accountType === 'invest') {
      const existingAssets =
        accountsRests?.invest?.accounts
          .map(account => account.balanceAsset?.code)
          .filter((code): code is string => !!code) || [];
      const pendingAssets = pendingAccounts
        .filter(account => account.accountType === 'invest')
        .map(account => account.assetCode);
      return [...existingAssets, ...pendingAssets];
    }

    return [];
  }, [
    accountType,
    accountsRests?.invest,
    accountsRests?.earn,
    pendingAccounts,
  ]);

  const getAssetSelectItems = (assets: Array<string> | undefined) => {
    if (!assets) return [];

    const shouldFilter =
      (accountType === 'invest' || accountType === 'earn') &&
      accountSettings.allowedAccountsQuantity === 'one_account_each_asset';

    return assets
      .filter(asset => (shouldFilter ? !takenAssets.includes(asset) : true))
      .map(item => ({
        value: item,
        code: item,
        name: assetsData?.value.find(asset => asset.code === item)?.name || '',
        icon: assetsIconsData?.value[item] || undefined,
      }));
  };

  const getLeverageSelectItems = (leverage: Array<number> | undefined) => {
    if (!leverage) return [];
    return leverage.map(item => ({
      value: item.toString(),
      title: `1 : ${item}`,
    }));
  };

  return {
    accountSettings,
    formFields,
    getTypeSelectItems,
    getAssetSelectItems,
    getLeverageSelectItems,
  };
};

export { useOpenAccountFormData };
